import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { PlusIcon } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useCreateResumeForUser } from "@/client/services/admin";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  slug: z
    .string()
    .min(1, "Slug is required")
    .regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens"),
  visibility: z.enum(["public", "private"]).default("private"),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
};

export const CreateResumeDialog = ({ open, onOpenChange, userId, userName }: Props) => {
  const createResume = useCreateResumeForUser();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      slug: "",
      visibility: "private",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [open]);

  // Auto-generate slug from title
  const watchTitle = form.watch("title");
  useEffect(() => {
    if (watchTitle) {
      const slug = watchTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
      form.setValue("slug", slug);
    }
  }, [watchTitle]);

  const onSubmit = async (values: FormValues) => {
    await createResume.mutateAsync({
      userId,
      data: {
        title: values.title,
        slug: values.slug,
        visibility: values.visibility,
      },
    });

    onOpenChange(false);
    form.reset();
    // The employee's resume list will automatically refresh via query invalidation
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>{t`Create Resume`}</DialogTitle>
          <DialogDescription>
            {t`Create a new resume for ${userName}`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="title"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Title`}</FormLabel>
                  <FormControl>
                    <Input placeholder={t`e.g. Software Engineer CV`} {...field} />
                  </FormControl>
                  <FormDescription>{t`A descriptive title for the resume`}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="slug"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Slug`}</FormLabel>
                  <FormControl>
                    <Input placeholder="software-engineer-cv" {...field} />
                  </FormControl>
                  <FormDescription>{t`URL-friendly identifier (auto-generated from title)`}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="visibility"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Visibility`}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t`Select visibility`} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="private">{t`Private`}</SelectItem>
                      <SelectItem value="public">{t`Public`}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>{t`Public resumes can be shared via a link`}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t`Cancel`}
              </Button>
              <Button type="submit" disabled={createResume.isPending}>
                <PlusIcon className="mr-2" />
                {createResume.isPending ? t`Creating...` : t`Create Resume`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
